import { Component, lazy, memo, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import type { TableDefinition } from '../engine/tables/definition';
import { preloadPhysicsRuntime } from '../engine/physics/world';
import DebugCanvas from '../render2d/DebugCanvas';
import loadingStyles from '../ui/TableLoadingScreen.module.css';
import { waitForRendererCanvas } from '../ui/renderReady';

const loadTable3DScene = () => import('./Table3DScene');
const Table3DScene = lazy(loadTable3DScene);
const RENDERER_RETRY_DELAY_MS = 120;
const RENDERER_READINESS_MAX_FRAMES = 360;
const RENDERER_READINESS_MAX_WAIT_MS = 5500;

function ignoreWarmupFailure(promise: Promise<unknown>): void {
  void promise.catch(() => {
    // Idle chunk warmup is opportunistic. A transient network/deploy failure
    // must not surface as an unhandled rejection or block a later PLAY retry.
  });
}

export function supportsWebGL2(
  createCanvas?: () => { getContext(contextId: 'webgl2'): unknown | null },
): boolean {
  if (!createCanvas) return true;
  try {
    return Boolean(createCanvas().getContext('webgl2'));
  } catch {
    // Privacy modes, locked-down webviews, disabled GPU acceleration, or a bad
    // graphics driver can make context creation throw instead of returning null.
    return false;
  }
}

function browserSupportsWebGL2(): boolean {
  if (typeof document === 'undefined') return true;
  return supportsWebGL2(() => document.createElement('canvas'));
}

export function preload3DExperience(
  loadRenderer: () => Promise<unknown> = loadTable3DScene,
  loadPhysics: () => Promise<unknown> = preloadPhysicsRuntime,
  canUse3D: () => boolean = browserSupportsWebGL2,
): void {
  // User intent is a stronger signal than the staggered idle warmup below. Start
  // both large dependencies together when 3D is viable so hover/focus/pointer-
  // down time overlaps their network/module work before PLAY commits the UI
  // transition. Physics still warms on fallback browsers because Canvas2D play
  // uses the same simulation runtime, while an unusable Three chunk is skipped.
  if (canUse3D()) ignoreWarmupFailure(loadRenderer());
  ignoreWarmupFailure(loadPhysics());
}

interface VisibilityDocument {
  visibilityState: DocumentVisibilityState;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

export function runWhenDocumentVisible(
  documentLike: VisibilityDocument,
  start: () => void,
): void {
  if (documentLike.visibilityState !== 'hidden') {
    start();
    return;
  }

  let started = false;
  const onVisibilityChange = () => {
    if (started || documentLike.visibilityState === 'hidden') return;
    started = true;
    documentLike.removeEventListener('visibilitychange', onVisibilityChange);
    start();
  };
  documentLike.addEventListener('visibilitychange', onVisibilityChange);
}

export function visibleWarmupTask(
  documentLike: VisibilityDocument,
  start: () => void,
): () => void {
  return () => runWhenDocumentVisible(documentLike, start);
}

export interface VisibleRendererWait {
  promise: Promise<boolean>;
  cancel: () => void;
}

function passiveAbortSignal(): AbortSignal {
  // Older engines can lack AbortController while the code path still benefits
  // from one consistent signal-shaped contract. This signal never aborts; stale
  // attempt results are rejected by the generation guard below instead.
  return {
    aborted: false,
    reason: undefined,
    onabort: null,
    addEventListener: () => undefined,
    removeEventListener: () => undefined,
    dispatchEvent: () => true,
    throwIfAborted: () => undefined,
  } as AbortSignal;
}

export function waitForVisibleRenderer(
  documentLike: VisibilityDocument,
  waitForRenderer: (signal: AbortSignal) => Promise<boolean>,
): VisibleRendererWait {
  let settled = false;
  let generation = 0;
  let attemptController: AbortController | null = null;
  let retryTimeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
  let resolveWait: (ready: boolean) => void = () => undefined;
  const promise = new Promise<boolean>((resolve) => {
    resolveWait = resolve;
  });

  const stopAttempt = () => {
    attemptController?.abort();
    attemptController = null;
  };

  const stopRetry = () => {
    if (retryTimeoutId !== undefined) globalThis.clearTimeout(retryTimeoutId);
    retryTimeoutId = undefined;
  };

  const finish = (ready: boolean) => {
    if (settled) return;
    settled = true;
    stopRetry();
    stopAttempt();
    documentLike.removeEventListener('visibilitychange', onVisibilityChange);
    resolveWait(ready);
  };

  const scheduleRetry = () => {
    if (settled || documentLike.visibilityState === 'hidden' || retryTimeoutId !== undefined) return;
    retryTimeoutId = globalThis.setTimeout(() => {
      retryTimeoutId = undefined;
      startAttempt();
    }, RENDERER_RETRY_DELAY_MS);
  };

  const startAttempt = () => {
    if (settled || documentLike.visibilityState === 'hidden') return;
    stopRetry();
    stopAttempt();
    const attempt = ++generation;
    const controller = typeof AbortController === 'undefined' ? null : new AbortController();
    const signal = controller?.signal ?? passiveAbortSignal();
    attemptController = controller;
    void waitForRenderer(signal)
      .then((ready) => {
        if (
          settled
          || attempt !== generation
          || signal.aborted
          || documentLike.visibilityState === 'hidden'
        ) return;
        attemptController = null;
        if (ready) finish(true);
        else scheduleRetry();
      })
      .catch(() => {
        if (
          settled
          || attempt !== generation
          || signal.aborted
          || documentLike.visibilityState === 'hidden'
        ) return;
        attemptController = null;
        scheduleRetry();
      });
  };

  const onVisibilityChange = () => {
    if (settled) return;
    if (documentLike.visibilityState === 'hidden') {
      // Invalidate an obsolete readiness attempt even when AbortController is
      // unavailable. Modern browsers also actively abort the bounded wait; older
      // engines simply ignore its eventual result through the generation guard.
      generation += 1;
      stopRetry();
      stopAttempt();
      return;
    }
    startAttempt();
  };

  documentLike.addEventListener('visibilitychange', onVisibilityChange);
  startAttempt();

  return {
    promise,
    cancel: () => {
      if (settled) return;
      settled = true;
      generation += 1;
      stopRetry();
      stopAttempt();
      documentLike.removeEventListener('visibilitychange', onVisibilityChange);
      resolveWait(false);
    },
  };
}

export function waitFor3DRendererCanvas(
  root: Pick<HTMLElement, 'querySelector'> | null,
  signal?: AbortSignal,
  waitForRenderer: typeof waitForRendererCanvas = waitForRendererCanvas,
): Promise<boolean> {
  // The renderer itself can spend up to ~1.5s compiling and the outer gate then
  // needs a 1.7s stable/adaptive-DPR window. A 3.5s attempt ceiling leaves almost
  // no room for real slow-device hitches and can trigger an expensive retry that
  // starts the settle window over. 5.5s is only a ceiling: fast devices still
  // return as soon as they are stable.
  return waitForRenderer(
    root,
    RENDERER_READINESS_MAX_FRAMES,
    undefined,
    RENDERER_READINESS_MAX_WAIT_MS,
    undefined,
    undefined,
    signal,
  );
}

class Table3DSceneBoundary extends Component<
  { children: ReactNode; onFailure: () => void },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(): void {
    // React rendering errors are contained here so a WebGL/driver failure cannot
    // take down the game shell. The parent swaps in the native Canvas2D renderer.
    this.props.onFailure();
  }

  render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}

// Keep Three.js/R3F and Rapier off the critical homepage dependency graph, but
// opportunistically move their download/module-parse work ahead of PLAY. The
// two large chunks are deliberately staggered so they do not create one giant
// post-paint main-thread burst on slower devices. No physics world is built here.
if (typeof window !== 'undefined') {
  const scheduleWarmup = () => {
    const startRendererWarmup = () => {
      if (browserSupportsWebGL2()) ignoreWarmupFailure(loadTable3DScene());
    };
    const startPhysicsWarmup = () => { ignoreWarmupFailure(preloadPhysicsRuntime()); };
    // Visibility can change after scheduling but before these idle callbacks run.
    // Re-check at execution time so background tabs do not spend CPU/bandwidth
    // parsing optional Three/R3F or Rapier chunks until the player returns.
    const warmRenderer = typeof document === 'undefined'
      ? startRendererWarmup
      : visibleWarmupTask(document, startRendererWarmup);
    const warmPhysics = typeof document === 'undefined'
      ? startPhysicsWarmup
      : visibleWarmupTask(document, startPhysicsWarmup);
    const requestIdle = window.requestIdleCallback?.bind(window);

    if (requestIdle) {
      requestIdle(warmRenderer, { timeout: 450 });
      globalThis.setTimeout(() => {
        requestIdle(warmPhysics, { timeout: 1400 });
      }, 700);
    } else {
      globalThis.setTimeout(warmRenderer, 180);
      globalThis.setTimeout(warmPhysics, 900);
    }
  };

  if (typeof document !== 'undefined') {
    runWhenDocumentVisible(document, scheduleWarmup);
  } else {
    scheduleWarmup();
  }
}

export interface Table3DShowroomProps {
  table: TableDefinition;
}

export interface RenderBudgetEnvironment {
  innerWidth: number;
  matchMedia?(query: string): { matches: boolean };
}

export interface CompatibleMediaQueryList {
  addEventListener?(type: 'change', listener: () => void): void;
  removeEventListener?(type: 'change', listener: () => void): void;
  addListener?(listener: () => void): void;
  removeListener?(listener: () => void): void;
}

export function subscribeMediaQueryChange(
  media: CompatibleMediaQueryList,
  listener: () => void,
): () => void {
  if (typeof media.addEventListener === 'function') {
    media.addEventListener('change', listener);
    return () => media.removeEventListener?.('change', listener);
  }

  // Safari before MediaQueryList inherited EventTarget (and some embedded
  // webviews) only exposes addListener/removeListener. Keep 3D render-budget
  // detection functional there instead of throwing during the mount effect.
  media.addListener?.(listener);
  return () => media.removeListener?.(listener);
}

export interface RendererReadiness {
  table: TableDefinition | null;
  mobileRenderBudget: boolean | null;
  ready: boolean;
}

export function rendererReadyForScene(
  readiness: RendererReadiness,
  table: TableDefinition,
  mobileRenderBudget: boolean,
): boolean {
  return readiness.ready
    && readiness.table === table
    && readiness.mobileRenderBudget === mobileRenderBudget;
}

export function usesMobile3DRenderBudget(environment: RenderBudgetEnvironment): boolean {
  return environment.matchMedia?.('(pointer: coarse)').matches === true || environment.innerWidth <= 760;
}

export function sameTable3DShowroomProps(
  previous: Table3DShowroomProps,
  next: Table3DShowroomProps,
): boolean {
  return previous.table === next.table;
}

function readMobileRenderBudget(): boolean {
  if (typeof window === 'undefined') return false;
  return usesMobile3DRenderBudget(window);
}

function Table3DShowroom({ table }: Table3DShowroomProps) {
  const [mobileRenderBudget, setMobileRenderBudget] = useState(readMobileRenderBudget);
  const [threeUnavailable, setThreeUnavailable] = useState(() => !browserSupportsWebGL2());
  const [rendererReadiness, setRendererReadiness] = useState<RendererReadiness>({
    table: null,
    mobileRenderBudget: null,
    ready: false,
  });
  const rootRef = useRef<HTMLDivElement>(null);
  const rendererReady = threeUnavailable
    || rendererReadyForScene(rendererReadiness, table, mobileRenderBudget);

  useEffect(() => {
    const media = window.matchMedia?.('(pointer: coarse)');
    const refresh = () => setMobileRenderBudget(readMobileRenderBudget());

    window.addEventListener('resize', refresh);
    const unsubscribeMedia = media ? subscribeMediaQueryChange(media, refresh) : () => undefined;
    refresh();

    return () => {
      window.removeEventListener('resize', refresh);
      unsubscribeMedia();
    };
  }, []);

  useEffect(() => {
    if (threeUnavailable) return undefined;
    let active = true;
    const readinessWait = typeof document === 'undefined'
      ? {
          promise: waitFor3DRendererCanvas(rootRef.current),
          cancel: () => undefined,
        }
      : waitForVisibleRenderer(
          document,
          (signal) => waitFor3DRendererCanvas(rootRef.current, signal),
        );

    void readinessWait.promise.then((ready) => {
      if (active && ready) {
        setRendererReadiness({ table, mobileRenderBudget, ready: true });
      }
    });
    return () => {
      active = false;
      readinessWait.cancel();
    };
  }, [table, mobileRenderBudget, threeUnavailable]);

  return (
    <div
      ref={rootRef}
      aria-busy={!rendererReady}
      data-renderer-fallback={threeUnavailable ? '2d' : undefined}
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      {threeUnavailable ? (
        <DebugCanvas table={table} />
      ) : (
        <Suspense fallback={null}>
          <Table3DSceneBoundary onFailure={() => setThreeUnavailable(true)}>
            <Table3DScene key={mobileRenderBudget ? 'mobile' : 'desktop'} table={table} />
          </Table3DSceneBoundary>
        </Suspense>
      )}
      {!rendererReady && (
        <div className={loadingStyles.screen} role="status" aria-live="polite" aria-label={`Preparing 3D ${table.name}`}>
          <div className={loadingStyles.card}>
            <img src="/brand/logo-square.webp" alt="" aria-hidden="true" />
            <div className={loadingStyles.spinner} aria-hidden="true" />
            <strong>PREPARING 3D {table.name.toUpperCase()}</strong>
            <div className={loadingStyles.progressRow}>
              <progress className={loadingStyles.progress} max={100} aria-label="Preparing 3D renderer" />
              <span className={loadingStyles.percent}>…</span>
            </div>
            <span>Setting up the table…</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(Table3DShowroom, sameTable3DShowroomProps);
